import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';

interface EmailPreferenceCategory {
  id: string;
  label: string;
  description: string;
}

interface EmailPreferenceResponse {
  email: string;
  categories: EmailPreferenceCategory[];
  preferences: Record<string, boolean>;
  unsubscribedAt: string | null;
  updatedAt: string | null;
}

export function EmailPreferencesPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<EmailPreferenceResponse | null>(null);
  const [preferences, setPreferences] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const unsubscribeMode = searchParams.get('mode') === 'unsubscribe';
  const encodedToken = token ? encodeURIComponent(token) : '';

  useEffect(() => {
    let cancelled = false;

    async function loadPreferences() {
      if (!encodedToken) {
        setError('This email preferences link is invalid.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/email/preferences/${encodedToken}`);
        const body = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(body?.error || 'Could not load email preferences.');
        }

        if (!cancelled) {
          setData(body);
          setPreferences(body.preferences);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load email preferences.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadPreferences();

    return () => {
      cancelled = true;
    };
  }, [encodedToken]);

  const isDirty = useMemo(() => {
    if (!data) return false;
    return data.categories.some((category) => preferences[category.id] !== data.preferences[category.id]);
  }, [data, preferences]);

  async function savePreferences(nextPreferences: Record<string, boolean>, unsubscribeAll = false) {
    if (!encodedToken) return;

    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      const response = await fetch(`/api/email/preferences/${encodedToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferences: nextPreferences,
          unsubscribeAll,
        }),
      });
      const body = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(body?.error || 'Could not save email preferences.');
      }

      setData(body);
      setPreferences(body.preferences);
      setMessage(unsubscribeAll ? 'You are unsubscribed from all email categories.' : 'Email preferences saved.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save email preferences.');
    } finally {
      setSaving(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    savePreferences(preferences);
  }

  function setAll(enabled: boolean) {
    if (!data) return;
    const nextPreferences = data.categories.reduce((next, category) => ({
      ...next,
      [category.id]: enabled,
    }), {} as Record<string, boolean>);

    setPreferences(nextPreferences);
  }

  return (
    <div className="min-h-screen bg-bg pt-[88px]">
      <NavBar variant="light" />

      <main className="page-container py-12">
        <div className="mx-auto max-w-3xl">
          <div className="mb-8">
            <div className="page-eyebrow">Email</div>
            <h1 className="page-heading">Email preferences</h1>
            <p className="mt-4 max-w-2xl text-[17px] leading-8 text-text-dim">
              Manage the LifeDrawing.live emails sent to this address. No sign in is required from this link.
            </p>
          </div>

          {loading ? (
            <div className="rounded-xl border border-line bg-white p-6 text-sm text-text-dim">
              Loading email preferences.
            </div>
          ) : error && !data ? (
            <div className="rounded-xl border border-line bg-white p-6">
              <h2 className="font-display text-2xl font-normal text-text">This link cannot be used.</h2>
              <p className="mt-3 text-sm leading-6 text-text-dim">{error}</p>
              <Link to="/" className="btn btn-secondary mt-6">Back to LifeDrawing.live</Link>
            </div>
          ) : data ? (
            <>
              <section className="mb-5 border-y border-line bg-white px-5 py-4">
                <div className="text-sm text-text-dim">Managing email for</div>
                <div className="mt-1 break-all font-medium text-text">{data.email}</div>
              </section>

              {unsubscribeMode && (
                <section className="mb-5 rounded-lg border border-line bg-panel p-5">
                  <h2 className="font-display text-2xl font-normal text-text">Unsubscribe from all email.</h2>
                  <p className="mt-2 text-sm leading-6 text-text-dim">
                    This will turn off every email category below for this address. You can turn individual categories back on later from this page.
                  </p>
                  <button
                    type="button"
                    className="btn btn-accent mt-5"
                    onClick={() => savePreferences(preferences, true)}
                    disabled={saving}
                  >
                    {saving ? 'Unsubscribing...' : 'Unsubscribe from all'}
                  </button>
                </section>
              )}

              <form onSubmit={handleSubmit} className="overflow-hidden rounded-xl border border-line bg-white">
                <div className="border-b border-line px-5 py-4">
                  <h2 className="font-display text-2xl font-normal text-text">Choose what to receive.</h2>
                  <p className="mt-2 text-sm leading-6 text-text-dim">
                    These categories match the emails LifeDrawing.live currently sends.
                  </p>
                </div>

                <div className="divide-y divide-line-soft">
                  {data.categories.map((category) => (
                    <label key={category.id} className="grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] gap-4 px-5 py-5 hover:bg-bg-secondary/60">
                      <span>
                        <span className="block font-medium text-text">{category.label}</span>
                        <span className="mt-1 block text-sm leading-6 text-text-dim">{category.description}</span>
                      </span>
                      <input
                        type="checkbox"
                        checked={preferences[category.id] ?? true}
                        onChange={(event) => setPreferences((current) => ({
                          ...current,
                          [category.id]: event.target.checked,
                        }))}
                        className="mt-1 h-5 w-5 rounded border-line text-accent focus:ring-accent"
                      />
                    </label>
                  ))}
                </div>

                <div className="flex flex-col gap-3 border-t border-line bg-bg-secondary px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn btn-secondary" onClick={() => setAll(true)} disabled={saving}>
                      Turn all on
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => setAll(false)} disabled={saving}>
                      Turn all off
                    </button>
                  </div>
                  <button type="submit" className="btn btn-accent" disabled={saving || !isDirty}>
                    {saving ? 'Saving...' : 'Save preferences'}
                  </button>
                </div>
              </form>

              {(message || error) && (
                <div className={`mt-4 rounded-lg border px-4 py-3 text-sm ${error ? 'border-line bg-white text-text' : 'border-line bg-panel text-text-dim'}`}>
                  {error || message}
                </div>
              )}
            </>
          ) : null}
        </div>
      </main>

      <SharedFooter />
    </div>
  );
}
